import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import axios from 'axios';
import {
  Search, Users, Calendar, FileText, AlertTriangle, MessageSquare,
  Video, ChevronRight, X, Phone, Mail, Clock, Shield, UserX, Ban, CheckCircle2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminCaregivers() {
  const [caregivers, setCaregivers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [selectedId, setSelectedId] = useState('');
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [confirmAction, setConfirmAction] = useState(null);
  const navigate = useNavigate();

  useEffect(() => { fetchCaregivers(); }, []);

  const fetchCaregivers = async (q = '') => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/caregivers${q ? `?search=${encodeURIComponent(q)}` : ''}`, { withCredentials: true });
      setCaregivers(data);
    } catch { toast.error('Failed to load caregivers'); }
    setLoading(false);
  };

  const fetchDetail = async (id) => {
    setDetailLoading(true);
    setSelectedId(id);
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/caregivers/${id}`, { withCredentials: true });
      setDetail(data);
    } catch { toast.error('Failed to load details'); }
    setDetailLoading(false);
  };

  const handleSearch = (e) => { e.preventDefault(); fetchCaregivers(search); };

  const handleSuspend = async (cgId) => {
    try {
      const { data } = await axios.put(`${API_URL}/api/admin/caregivers/${cgId}/suspend`, {}, { withCredentials: true });
      toast.success(data.message);
      fetchCaregivers(search);
      if (selectedId === cgId) fetchDetail(cgId);
      setConfirmAction(null);
    } catch (err) { toast.error(err.response?.data?.detail || 'Action failed'); }
  };

  const handleRemove = async (cgId) => {
    try {
      await axios.delete(`${API_URL}/api/admin/caregivers/${cgId}/remove`, { withCredentials: true });
      toast.success('Caregiver removed from network');
      fetchCaregivers(search);
      if (selectedId === cgId) { setSelectedId(''); setDetail(null); }
      setConfirmAction(null);
    } catch (err) { toast.error(err.response?.data?.detail || 'Action failed'); }
  };

  const handleDropdownSelect = (id) => {
    if (id) fetchDetail(id);
  };

  const filtered = statusFilter === 'all' ? caregivers : caregivers.filter(c => c.status === statusFilter);

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-caregivers-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground">Caregivers</h1>
            <p className="text-muted-foreground mt-1">{caregivers.length} caregiver(s) in your agency</p>
          </div>
          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, email, USIC..."
                className="pl-9 bg-muted border-border text-foreground w-64"
                data-testid="caregiver-search"
              />
            </div>
            <Button type="submit" variant="outline" className="border-border text-foreground">Search</Button>
          </form>
        </div>

        {/* Quick Select Dropdown + Status Filter */}
        <div className="flex gap-3 flex-wrap">
          <Select value={selectedId} onValueChange={handleDropdownSelect}>
            <SelectTrigger className="w-72 bg-card border-border text-foreground" data-testid="caregiver-dropdown">
              <SelectValue placeholder="Select a caregiver..." />
            </SelectTrigger>
            <SelectContent className="max-h-64">
              {caregivers.map(cg => (
                <SelectItem key={cg.id} value={cg.id}>
                  <span className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${cg.status === 'active' ? 'bg-emerald-500' : cg.status === 'suspended' ? 'bg-red-500' : 'bg-zinc-400'}`} />
                    {cg.name}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex gap-1">
            {['all', 'active', 'suspended'].map(f => (
              <Button key={f} size="sm" variant={statusFilter === f ? 'default' : 'ghost'}
                className={statusFilter === f ? 'bg-[#00A6A0] text-white' : 'text-muted-foreground'}
                onClick={() => setStatusFilter(f)} data-testid={`filter-status-${f}`}
              >
                {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
              </Button>
            ))}
          </div>
        </div>

        <div className="flex gap-6">
          {/* List */}
          <div className={`${selectedId ? 'hidden lg:block lg:w-2/5' : 'w-full'} space-y-2`}>
            {loading ? (
              <div className="flex justify-center py-12"><div className="spinner w-8 h-8" /></div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-12 glass-card rounded-xl border border-border">
                <Users className="w-12 h-12 mx-auto mb-3 text-muted-foreground" />
                <p className="text-muted-foreground">No caregivers found</p>
              </div>
            ) : (
              filtered.map((cg) => (
                <div
                  key={cg.id}
                  onClick={() => { setSelectedId(cg.id); fetchDetail(cg.id); }}
                  className={`p-4 rounded-xl border cursor-pointer transition-all ${
                    selectedId === cg.id ? 'border-[#00A6A0] bg-[#00A6A0]/5' : 'border-border glass-card hover:border-muted-foreground/30'
                  }`}
                  data-testid={`caregiver-row-${cg.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                        cg.status === 'suspended' ? 'bg-red-500/10' : 'bg-[#00A6A0]/10'
                      }`}>
                        <span className={`font-bold ${cg.status === 'suspended' ? 'text-red-500' : 'text-[#00A6A0]'}`}>{cg.name?.charAt(0)?.toUpperCase()}</span>
                      </div>
                      <div className="min-w-0">
                        <p className="text-foreground font-medium truncate">{cg.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{cg.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      {cg.expiring_documents > 0 && (
                        <span className="px-2 py-0.5 rounded-full text-xs bg-amber-500/15 text-amber-400 flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" />{cg.expiring_documents}
                        </span>
                      )}
                      <span className={`px-2 py-0.5 rounded-full text-xs ${
                        cg.status === 'active' ? 'bg-emerald-500/15 text-emerald-500' : cg.status === 'suspended' ? 'bg-red-500/15 text-red-500' : 'bg-zinc-500/15 text-zinc-400'
                      }`}>
                        {cg.status}
                      </span>
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    </div>
                  </div>
                  <div className="flex gap-4 mt-2 text-xs text-muted-foreground">
                    <span>{cg.active_shifts} active shifts</span>
                    <span>{cg.assigned_clients} client(s)</span>
                    <span>{cg.total_documents} docs</span>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Detail Panel */}
          {selectedId && (
            <div className="flex-1 min-w-0" data-testid="caregiver-detail-panel">
              <div className="sticky top-20">
                {detailLoading ? (
                  <div className="flex justify-center py-12"><div className="spinner w-8 h-8" /></div>
                ) : detail ? (
                  <div className="space-y-4">
                    {/* Header */}
                    <Card className="glass-card border-border">
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center gap-4">
                            <div className={`w-14 h-14 rounded-full flex items-center justify-center ${
                              detail.status === 'suspended' ? 'bg-red-500/10' : 'bg-[#00A6A0]/10'
                            }`}>
                              <span className={`font-bold text-xl ${detail.status === 'suspended' ? 'text-red-500' : 'text-[#00A6A0]'}`}>{detail.name?.charAt(0)?.toUpperCase()}</span>
                            </div>
                            <div>
                              <h2 className="text-xl font-heading font-bold text-foreground">{detail.name}</h2>
                              <p className="text-sm text-muted-foreground">{detail.usic} | {detail.staff_number}</p>
                              <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs ${
                                detail.status === 'active' ? 'bg-emerald-500/15 text-emerald-500' : detail.status === 'suspended' ? 'bg-red-500/15 text-red-500' : 'bg-zinc-500/15 text-zinc-400'
                              }`}>
                                {detail.status || 'active'}
                              </span>
                            </div>
                          </div>
                          <Button variant="ghost" size="icon" onClick={() => { setSelectedId(''); setDetail(null); }} className="lg:hidden">
                            <X className="w-5 h-5" />
                          </Button>
                        </div>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div className="flex items-center gap-2 text-muted-foreground"><Mail className="w-4 h-4" />{detail.email}</div>
                          <div className="flex items-center gap-2 text-muted-foreground"><Phone className="w-4 h-4" />{detail.phone || 'Not set'}</div>
                        </div>
                        <div className="flex gap-2 mt-4 flex-wrap">
                          <Button size="sm" className="bg-[#00A6A0] hover:bg-[#00A6A0]/90 text-white gap-2" onClick={() => navigate('/messaging')} data-testid="msg-caregiver-btn">
                            <MessageSquare className="w-4 h-4" />Message
                          </Button>
                          <Button size="sm" variant="outline" className="border-border text-foreground gap-2" onClick={() => navigate('/video')} data-testid="call-caregiver-btn">
                            <Video className="w-4 h-4" />Video Call
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className={`gap-2 ${detail.status === 'suspended' ? 'border-emerald-500/30 text-emerald-500 hover:bg-emerald-500/10' : 'border-amber-500/30 text-amber-500 hover:bg-amber-500/10'}`}
                            onClick={() => setConfirmAction({ type: 'suspend', id: detail.id || selectedId, name: detail.name, currentStatus: detail.status })}
                            data-testid="suspend-caregiver-btn"
                          >
                            {detail.status === 'suspended' ? <><CheckCircle2 className="w-4 h-4" />Reactivate</> : <><Ban className="w-4 h-4" />Suspend</>}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-red-500/30 text-red-500 hover:bg-red-500/10 gap-2"
                            onClick={() => setConfirmAction({ type: 'remove', id: detail.id || selectedId, name: detail.name })}
                            data-testid="remove-caregiver-btn"
                          >
                            <UserX className="w-4 h-4" />Remove
                          </Button>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Assigned Clients */}
                    <Card className="glass-card border-border">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm text-muted-foreground uppercase tracking-wider">Assigned Clients ({detail.assigned_clients?.length || 0})</CardTitle>
                      </CardHeader>
                      <CardContent>
                        {detail.assigned_clients?.length === 0 ? (
                          <p className="text-sm text-muted-foreground py-2">No clients assigned yet</p>
                        ) : (
                          <div className="space-y-2">
                            {detail.assigned_clients?.map((cl) => (
                              <div key={cl.id} className="flex items-center justify-between p-3 rounded-lg bg-muted">
                                <div>
                                  <p className="text-foreground font-medium text-sm">{cl.name}</p>
                                  <p className="text-xs text-muted-foreground">{cl.email}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    {/* Recent Shifts & Timesheets */}
                    <div className="grid grid-cols-2 gap-4">
                      <Card className="glass-card border-border">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                            <Calendar className="w-4 h-4" />Recent Shifts
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          {detail.recent_shifts?.length === 0 ? (
                            <p className="text-sm text-muted-foreground py-2">No shifts</p>
                          ) : (
                            <div className="space-y-1.5">
                              {detail.recent_shifts?.slice(0, 5).map((s, i) => (
                                <div key={i} className="flex items-center justify-between text-xs p-2 rounded bg-muted">
                                  <span className="text-foreground">{s.date || 'N/A'}</span>
                                  <span className={`px-1.5 py-0.5 rounded text-xs ${s.status === 'completed' ? 'bg-emerald-500/15 text-emerald-500' : 'bg-amber-500/15 text-amber-400'}`}>{s.status}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                      <Card className="glass-card border-border">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                            <Clock className="w-4 h-4" />Timesheets
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          {detail.timesheets?.length === 0 ? (
                            <p className="text-sm text-muted-foreground py-2">No timesheets</p>
                          ) : (
                            <div className="space-y-1.5">
                              {detail.timesheets?.map((t, i) => (
                                <div key={i} className="flex items-center justify-between text-xs p-2 rounded bg-muted">
                                  <span className="text-foreground">{t.period_start} - {t.period_end}</span>
                                  <span className={`px-1.5 py-0.5 rounded text-xs ${t.status === 'approved' ? 'bg-emerald-500/15 text-emerald-500' : 'bg-amber-500/15 text-amber-400'}`}>{t.status}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </div>

                    {/* Documents */}
                    <Card className="glass-card border-border">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                          <FileText className="w-4 h-4" />Documents ({detail.documents?.length || 0})
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {detail.documents?.length === 0 ? (
                          <p className="text-sm text-muted-foreground py-2">No documents uploaded</p>
                        ) : (
                          <div className="space-y-1.5">
                            {detail.documents?.map((d, i) => (
                              <div key={i} className="flex items-center justify-between text-xs p-2 rounded bg-muted">
                                <span className="text-foreground">{d.document_type || d.name || 'Document'}</span>
                                {d.expiry_date && (
                                  <span className={`px-1.5 py-0.5 rounded text-xs ${new Date(d.expiry_date) < new Date(Date.now() + 30*86400000) ? 'bg-amber-500/15 text-amber-400' : 'bg-emerald-500/15 text-emerald-500'}`}>
                                    Exp: {new Date(d.expiry_date).toLocaleDateString()}
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                ) : null}
              </div>
            </div>
          )}
        </div>

        {/* Confirm Action Dialog */}
        <Dialog open={!!confirmAction} onOpenChange={() => setConfirmAction(null)}>
          <DialogContent className="glass-card border-border sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-foreground">
                {confirmAction?.type === 'suspend'
                  ? (confirmAction?.currentStatus === 'suspended' ? 'Reactivate Caregiver?' : 'Suspend Caregiver?')
                  : 'Remove Caregiver?'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {confirmAction?.type === 'suspend'
                  ? confirmAction?.currentStatus === 'suspended'
                    ? `Reactivate ${confirmAction?.name}? They will regain access to shifts and the platform.`
                    : `Suspend ${confirmAction?.name}? They will be unable to view shifts or clock in until reactivated.`
                  : `Remove ${confirmAction?.name} from your network? Their assigned shifts will be unassigned. This action cannot be undone.`}
              </p>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1 border-border text-foreground" onClick={() => setConfirmAction(null)} data-testid="cancel-action-btn">Cancel</Button>
                <Button
                  className={`flex-1 text-white ${confirmAction?.type === 'remove' ? 'bg-red-500 hover:bg-red-600' : confirmAction?.currentStatus === 'suspended' ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-amber-500 hover:bg-amber-600'}`}
                  onClick={() => {
                    if (confirmAction?.type === 'suspend') handleSuspend(confirmAction.id);
                    else handleRemove(confirmAction.id);
                  }}
                  data-testid="confirm-action-btn"
                >
                  {confirmAction?.type === 'suspend'
                    ? (confirmAction?.currentStatus === 'suspended' ? 'Reactivate' : 'Suspend')
                    : 'Remove'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
